/** 角色枚举 */
export enum Role {
  PLATFORM_ADMIN = 'platform_admin',
  TENANT_ADMIN = 'tenant_admin',
  AMOEBA_LEADER = 'amoeba_leader',
  MEMBER = 'member',
}

/** 科目类型枚举 */
export enum AccountType {
  REVENUE = 'revenue',
  EXPENSE = 'expense',
}

/** 借贷方向枚举 */
export enum Direction {
  DEBIT = 'debit',
  CREDIT = 'credit',
}

/** 套餐枚举 */
export enum Plan {
  FREE = 'free',
  BASIC = 'basic',
  PRO = 'pro',
}

/** 侧边栏导航模块 */
export interface NavModule {
  key: string;
  label: string;
  icon: string;
  path: string;
  enabled: boolean;
  external?: boolean; // 外链：点击触发浏览器整页跳转（用于 SPA 之外的静态页，如海报生成器）
  children?: NavModule[]; // 子菜单
  /**
   * 允许访问的角色；省略表示所有登录用户可见。
   * 特殊值 'consultant'：认证咨询师可见（不属于租户角色体系，由 authStore.isConsultant 判定）。
   */
  requiredRoles?: string[];
  /**
   * 授权渠道可见：平台管理员可授权指定账号（通常是兼作渠道的租户管理员）查看
   * 其直接下线的利润自测线索。与 requiredRoles 为「或」关系（满足任一即可见）。
   */
  requireChannel?: boolean;
}

/** 九大模块定义（经营会计为父级菜单，含子页面） */
export const NAV_MODULES: NavModule[] = [
  { key: 'workbench', label: '我的工作台', icon: 'ViewKanban', path: '/workbench', enabled: true },
  // 审核中心（v2.2.53）：聚合所有平台级待审队列，仅平台管理员可见
  { key: 'review-center', label: '审核中心', icon: 'Gavel', path: '/review-center', enabled: true, requiredRoles: ['platform_admin'] },
  { key: 'dashboard',  label: '总裁驾驶舱', icon: 'Dashboard',   path: '/',                   enabled: true },
  {
    key: 'amoeba', label: '阿米巴组织', icon: 'AccountTree', path: '/amoeba/structure', enabled: true,
    children: [
      { key: 'amoeba-structure', label: '架构设置',    icon: 'AccountTree', path: '/amoeba/structure', enabled: true },
      { key: 'amoeba-orgchart',  label: '组织可视化',  icon: 'AccountTree', path: '/amoeba/org-chart',  enabled: true },
      { key: 'amoeba-member',    label: '人员配置',    icon: 'People',      path: '/amoeba/personnel-config', enabled: true },
    ],
  },
  {
    key: 'account', label: '经营会计', icon: 'ReceiptLong', path: '/account/define', enabled: true,
    children: [
      { key: 'account-define',     label: '科目定义', icon: 'ListAlt',       path: '/account/define',        enabled: true },
      { key: 'account-config',     label: '科目配置', icon: 'Tune',           path: '/account/config',        enabled: true },
      { key: 'transactions',       label: '内部交易与定价', icon: 'SwapHoriz',      path: '/account/transactions',  enabled: true },
      { key: 'allocation-rules',    label: '总部分摊', icon: 'AccountBalance',  path: '/account/allocation',     enabled: true },
      { key: 'templates-market',   label: '模板市场', icon: 'Storefront',     path: '/account/templates-market', enabled: true },
    ],
  },
  {
    key: 'product-cost', label: '产品成本管理', icon: 'Inventory', path: '/account/bom-import', enabled: true,
    children: [
      { key: 'bom-import',     label: 'BOM成本导入',     icon: 'Description',  path: '/account/bom-import',     enabled: true },
      { key: 'labor-cost',     label: '人工成本管理',     icon: 'Badge',         path: '/account/labor-cost',    enabled: true },
      { key: 'energy-cost',   label: '能源成本管理',     icon: 'Bolt',          path: '/account/energy-cost',  enabled: true },
      { key: 'product-report', label: '产品成本报表',     icon: 'Assessment',    path: '/account/product-report', enabled: true },
    ],
  },
  {
    // 报价中心：统一父组，下挂三个子模块（工作台/看板/配置）。
    key: 'quotation-center', label: '报价中心', icon: 'RequestQuote', path: '/account/quotation', enabled: true,
    children: [
      {
        // 复合页：把「客户需求 / 我要报价 / 我的报价单」三段流程收拢到同一页（内部 Tabs 串联），点击直达。
        key: 'quotation-workbench', label: '报价工作台', icon: 'RequestQuote', path: '/account/quotation', enabled: true,
      },
      {
        // 报价流程看板：按 flow_status 分列（询价/报价中/已出报价/已归档），可视化团队报价管线。
        key: 'quotation-board', label: '报价看板', icon: 'ViewColumn', path: '/account/quotation-board', enabled: true,
      },
      {
        // 报价相关的租户级配置（管理员）：三级子组（Sidebar 递归渲染支持嵌套）。
        key: 'quotation-config', label: '报价配置', icon: 'Tune', path: '/account/material-prices', enabled: true,
        children: [
          { key: 'material-prices', label: '物料单价管理',   icon: 'Sell',         path: '/account/material-prices',   enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
          { key: 'quotation-flow',  label: '报价流程',       icon: 'AccountTree',  path: '/account/quotation-flow',    enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
          { key: 'quotation-roles', label: '报价岗位配置',   icon: 'Badge',         path: '/account/quotation-roles',   enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
          { key: 'quotation-market', label: '报价模块市场',  icon: 'Storefront',    path: '/account/quotation-market',  enabled: true },
        ],
      },
    ],
  },
  {
    key: 'report', label: '经营报表', icon: 'Assessment', path: '/report/create', enabled: true,
    children: [
      { key: 'report-create',  label: '报表制定', icon: 'EditNote',    path: '/report/create',        enabled: true },
      { key: 'report-monthly', label: '报表查询', icon: 'Assessment',  path: '/report/monthly',       enabled: true },
      { key: 'report-company', label: '公司汇总', icon: 'Hub',          path: '/report/company',       enabled: true },
      { key: 'data-import',    label: '数据导入', icon: 'FileUpload',  path: '/account/import',        enabled: true },
      { key: 'sales-transactions', label: '销售孤岛入账', icon: 'Storefront', path: '/sales/transactions', enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
    ],
  },
  {
    key: 'target', label: '目标与预算', icon: 'TrackChanges', path: '/target/goal', enabled: true,
    children: [
      { key: 'target-goal', label: '经营目标', icon: 'TrackChanges', path: '/target/goal', enabled: true },
      { key: 'target-execution', label: '销售计划执行', icon: 'PlaylistAddCheck', path: '/target/execution', enabled: true },
    ],
  },
  {
    key: 'hr', label: '人力资源管理', icon: 'People', path: '/account/piecework-wage', enabled: true,
    children: [
      { key: 'piecework-wage', label: '计件工资核算', icon: 'Calculate', path: '/account/piecework-wage', enabled: true },
      { key: 'technician', label: '技工管理', icon: 'Engineering', path: '/account/technician', enabled: true },
      { key: 'performance', label: '绩效管理', icon: 'Stars', path: '/account/performance', enabled: true },
      { key: 'salary', label: '薪资管理', icon: 'AttachMoney', path: '/account/salary', enabled: true },
      { key: 'pk-module', label: '积分PK', icon: 'EmojiEvents', path: '/account/pk', enabled: true },
    ],
  },
  {
    key: 'approval', label: '流程中心', icon: 'Approval', path: '/approval/pending', enabled: true,
    children: [
      { key: 'approval-process-owner', label: '流程规划', icon: 'People', path: '/approval/process-owner', enabled: true },
      { key: 'approval-process',  label: '流程设计',      icon: 'AccountTree', path: '/approval/process', enabled: true },
      { key: 'approval-pending', label: '流程执行', icon: 'PlaylistAddCheck', path: '/approval/pending', enabled: true },
      { key: 'approval-stats',    label: '流程统计',      icon: 'BarChart',    path: '/approval/stats', enabled: true },
    ],
  },
  {
    // 客户关系管理：活动获客在前（办活动→报名→沉淀线索），再到客户跟进/客户分析
    key: 'crm', label: '客户关系管理', icon: 'People', path: '/opportunity', enabled: true,
    children: [
      { key: 'crm-activity-manage',   label: '活动管理', icon: 'Event',        path: '/activity-manage',   enabled: true },
      { key: 'crm-activity-analysis', label: '活动分析', icon: 'Insights',      path: '/activity-analysis', enabled: true },
      { key: 'crm-activity-list',     label: '活动列表', icon: 'Apps',         path: '/activities',        enabled: true },
      { key: 'crm-my-activities',     label: '我的活动', icon: 'Person',       path: '/my-activities',     enabled: true },
      { key: 'crm-speaker-activities', label: '主讲活动', icon: 'RecordVoiceOver', path: '/speaker-activities', enabled: true },
      { key: 'crm-follow',   label: '客户跟进', icon: 'Paid',       path: '/opportunity',       enabled: true },
      { key: 'crm-analysis', label: '客户分析', icon: 'Assessment', path: '/customer-analysis', enabled: true },
      // v2.2.84 方案 C：营销产品（打通 产品 ↔ 海报 ↔ 活动详情 联动）；管理端权限，仅租户/平台管理员可见
      { key: 'product-manage', label: '产品管理', icon: 'Inventory2', path: '/product-manage', enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
    ],
  },
  {
    // 咨询项目管理（CPM）v2.2.0：1 驾驶舱 + 5 过程组 + 财务/资源横切。
    // 设计要点：① 工时单位为人天（咨询业惯例，步长 0.5）；
    // ② 每个咨询项目即一个阿米巴核算单元，损益可按月过账进经营会计；
    // ③ 客户方只读门户（/portal/:token 免登录）单独路由，不在此菜单内。
    key: 'consulting', label: '咨询项目管理', icon: 'WorkOutline', path: '/consulting/projects', enabled: true, requiredRoles: ['consultant', 'platform_admin'],
    children: [
      { key: 'consulting-dashboard', label: '项目驾驶舱', icon: 'SpaceDashboard', path: '/consulting/dashboard', enabled: true },
      { key: 'consulting-projects',  label: '项目列表',   icon: 'FolderOpen',     path: '/consulting/projects', enabled: true },
      { key: 'consulting-timesheets', label: '我的工时',  icon: 'AccessTime',     path: '/consulting/timesheets', enabled: true },
      // 权限模板（RBAC 租户默认档）v2.2.2：仅平台管理员可见
      { key: 'consulting-permission-template', label: '权限模板', icon: 'AdminPanelSettings', path: '/consulting/permission-template', enabled: true, requiredRoles: ['platform_admin'] },
      // 咨询师知识分享库（Knowledge Sharing Library，v2.2.7）：仅开放给咨询项目上的顾问使用，
      // 作为 CPM 子模块挂载；全平台认证咨询师共享的交付模板/案例/方法论资料库。
      { key: 'knowledge', label: '知识分享库', icon: 'LibraryBooks', path: '/knowledge', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      // 结构化 FMEA 风险库（v2.2.21，C 方案）：与知识词条互补，条目形态可按阶段/严重度/RPN 检索并挂到项目
      { key: 'fmea-library', label: '风险库 FMEA', icon: 'WarningAmber', path: '/fmea/library', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      // 问卷引擎（v2.2.32）：沉淀的是答案而非题目 —— 模板设计 → 发起调查 → 匿名回收 → 逐题统计
      { key: 'survey-templates', label: '问卷模板', icon: 'Quiz', path: '/surveys/templates', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      { key: 'surveys', label: '调查管理', icon: 'Assessment', path: '/surveys', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
    ],
  },
  {
    // 学习与成长（商学院）v2.0.41：学员端「我的学习」全员可见；
    // 创作端（课程/试题/数据）仅认证咨询师与平台管理员可见。
    key: 'learning', label: '学习与成长', icon: 'School', path: '/learning/study', enabled: true,
    children: [
      { key: 'learning-study',   label: '我的学习',   icon: 'PlayCircle',    path: '/learning/study',   enabled: true },
      { key: 'learning-courses', label: '内容管理',   icon: 'VideoLibrary',  path: '/learning/courses', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      { key: 'learning-exams',   label: '试题管理',   icon: 'Quiz',          path: '/learning/exams',   enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      { key: 'learning-reviews', label: '评价与数据', icon: 'Insights',      path: '/learning/reviews', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      { key: 'learning-ebooks',  label: '专著商城',   icon: 'MenuBook',      path: '/learning/ebooks', enabled: true },
      { key: 'learning-ebook-orders', label: '我的订单', icon: 'LocalShipping', path: '/learning/ebook-orders', enabled: true },
      { key: 'learning-author-ebooks', label: '我的专著', icon: 'AutoStories', path: '/learning/author-ebooks', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      { key: 'learning-ebook-fulfillment', label: '纸质书履约', icon: 'LocalShipping', path: '/learning/ebook-fulfillment', enabled: true, requiredRoles: ['consultant', 'platform_admin'] },
      { key: 'learning-ebook-changes', label: '改价记录', icon: 'ReceiptLong', path: '/learning/admin/ebook-changes', enabled: true, requiredRoles: ['platform_admin'] },
    ],
  },
  {
    // 咨询师认证（KYC）v2.0.42：自助提交身份证/资格证/项目经验，平台审核。
    // 仅「咨询师身份」用户可见（requiredRoles='consultant_self'，由 authStore.identity 判定，
    // 不依赖 isConsultant，以便未通过认证(pending)的咨询师也能进入页面提交/查看进度）。
    key: 'consultant-cert', label: '咨询师认证', icon: 'VerifiedUser', path: '/consultant/certification', enabled: true,
    requiredRoles: ['consultant_self'],
  },
  {
    key: 'behavior-analytics', label: '用户使用分析', icon: 'Insights', path: '/analytics', enabled: true, requiredRoles: ['platform_admin'],
  },
  {
    key: 'settings', label: '系统设置', icon: 'Settings', path: '/settings/roles', enabled: true,
    children: [
      { key: 'settings-tenant-invitation', label: '成员邀请', icon: 'PersonAdd', path: '/settings/tenant-invitation', enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
      { key: 'settings-roles', label: '角色与权限', icon: 'AdminPanelSettings', path: '/settings/roles', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-consultants', label: '咨询师管理', icon: 'SupervisorAccount', path: '/settings/consultants', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-users', label: '用户管理', icon: 'People', path: '/settings/users', enabled: true, requiredRoles: ['tenant_admin', 'platform_admin'] },
      { key: 'settings-all-users', label: '全平台用户', icon: 'Badge', path: '/settings/all-users', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-profit-quiz-leads', label: '利润自测线索', icon: 'Quiz', path: '/settings/profit-quiz-leads', enabled: true, requiredRoles: ['platform_admin'], requireChannel: true },
      { key: 'settings-mail', label: '邮件中心', icon: 'MailOutline', path: '/settings/mail', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-points', label: '个人中心', icon: 'WorkspacePremium', path: '/settings/points', enabled: true },
      // v2.2.89 个人资料：所有登录用户可见，自助维护头像/昵称/简介/城市
      { key: 'profile', label: '个人资料', icon: 'Person', path: '/profile', enabled: true },
      { key: 'settings-problem-reports', label: '问题反馈管理', icon: 'Feedback', path: '/settings/problem-reports', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-announcements', label: '平台公告管理', icon: 'Campaign', path: '/settings/announcements', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-splash-screens', label: '登录闪屏管理', icon: 'Wallpaper', path: '/settings/splash-screens', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'settings-referral', label: '推荐返佣配置', icon: 'Share', path: '/settings/referral', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'enterprise',     label: '企业管理', icon: 'Domain', path: '/settings/enterprise', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'help-manage',    label: '帮助内容管理', icon: 'Article', path: '/settings/help', enabled: true, requiredRoles: ['platform_admin'] },
      { key: 'legal-manage',   label: '协议与隐私管理', icon: 'Gavel', path: '/settings/legal', enabled: true, requiredRoles: ['platform_admin'] },
      {
        // 海报生成器（v2.2.39）：SPA 之外的静态页，由 server/public/poster 下发（Express /poster 路由）。
        // v2.2.42：页内已加三模板切换导航（获客/直播/工具包话术），侧边栏合并为单一入口，不再重复列子项。
        key: 'poster-generator', label: '海报生成器', icon: 'AutoAwesome', path: '/poster/index.html', enabled: true, external: true,
      },
      {
        // 产品海报生成器（v2.2.83）：面向产品/解决方案的获客海报（痛点+产品亮点+三步旅程），同为 /poster 静态页。
        key: 'product-poster', label: '产品海报', icon: 'AutoAwesome', path: '/poster/product-poster.html', enabled: true, external: true,
      },
    ],
  },
  { key: 'help',       label: '帮助中心', icon: 'HelpOutline',  path: '/help',                  enabled: true },
  {
    // 站内通知中心（Phase B P3 第二部分）：顶栏铃铛为主要入口，此处提供完整列表页入口
    key: 'notifications', label: '通知中心', icon: 'Notifications', path: '/notifications', enabled: true,
  },
  {
    // 会员运营（v2.2.74）：抽奖激励模块 —— 引导看课/参训用户长期抽奖，送积分或合规现金券
    key: 'member-ops', label: '会员运营', icon: 'CardGiftcard', path: '/lottery', enabled: true,
    children: [
      { key: 'lottery-center', label: '抽奖中心', icon: 'CardGiftcard', path: '/lottery', enabled: true },
      { key: 'lottery-rewards', label: '我的奖励', icon: 'Redeem', path: '/lottery/rewards', enabled: true },
      { key: 'lottery-admin', label: '抽奖管理', icon: 'AdminPanelSettings', path: '/lottery/admin', enabled: true, requiredRoles: ['platform_admin'] },
    ],
  },
];

/** 阿米巴最大层级深度 */
export const MAX_AMOEBA_LEVEL = 10; // 0~10 共11层

/** 每页默认条数 */
export const DEFAULT_PAGE_SIZE = 20;

/** 工时常量 */
export const WORK_DAYS_PER_MONTH = 22;
export const WORK_HOURS_PER_DAY = 8;
